import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, PlayCircle, Loader2, SearchX, ArrowUpCircle, XCircle } from "lucide-react";

const SUP = ["supervisor", "watch_commander", "records_supervisor", "admin"];

// Call Type Coverage Dashboard + Run Coverage Audit + Possible-Missing-Call-Type
// review queue (EXPANSION 3 — additive).
export const CoveragePanel = () => {
  const { user } = useAuth();
  const isSup = SUP.includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [cov, setCov] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [misses, setMisses] = useState([]);
  const [promote, setPromote] = useState({}); // mid -> {label, discipline, priority}

  const load = () => {
    client.get("/call-types-coverage").then((r) => { setCov(r.data); setAudit(r.data.last_audit); }).catch(() => {});
    if (isSup) client.get("/call-type-misses").then((r) => setMisses(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line

  const runAudit = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/call-types-coverage/audit");
      setAudit(data);
      toast[data.result === "PASS" ? "success" : "warning"](`Coverage audit ${data.result}: ${data.total_checked} types, ${data.issues_count} issue(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Audit failed"); }
    finally { setBusy(false); }
  };

  const doPromote = async (m) => {
    const p = promote[m.mid] || {};
    const label = (p.label || m.term).trim();
    try {
      const { data } = await client.post(`/call-type-misses/${m.mid}/promote`, {
        label, discipline: p.discipline || "police", priority: Number(p.priority ?? 3), category: "CUSTOM",
      });
      toast.success(`New call type ${data.code} — "${data.label}" created (alias: ${m.term})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Promote failed"); }
  };

  const doDismiss = async (m) => {
    try { await client.post(`/call-type-misses/${m.mid}/dismiss`); toast.success("Dismissed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); }
  };

  const inp = "bg-[#141518] border border-[#2C2D35] px-2 py-1 text-[11px] outline-none focus:border-[#3B82F6] font-mono text-white rounded-sm";
  if (!cov) return <div className="p-4 font-mono text-xs text-[#9CA3AF]">Loading coverage…</div>;

  const cards = [
    ["Active call types", cov.total_active, "#3B82F6"],
    ["Police", cov.by_discipline.police, "#3B82F6"],
    ["Fire", cov.by_discipline.fire, "#EF4444"],
    ["EMS", cov.by_discipline.ems, "#10B981"],
    ["Death-investigation types", cov.death_types, "#A855F7"],
    ["Fallback types", cov.fallback_types, "#F59E0B"],
    ["Custom (admin-created)", cov.custom_types, "#22D3EE"],
    ["Missing question trees", cov.missing_questions, cov.missing_questions ? "#EF4444" : "#10B981"],
    ["Missing response rec.", cov.missing_dispatch, cov.missing_dispatch ? "#EF4444" : "#10B981"],
    ["Calls entered Unknown/Other", cov.unknown_or_other_calls, "#9CA3AF"],
    ["Searches with no result", cov.open_search_misses, cov.open_search_misses ? "#F59E0B" : "#10B981"],
  ];

  return (
    <div data-testid="coverage-panel" className="p-4 space-y-4 overflow-y-auto">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {cards.map(([label, val, color]) => (
          <div key={label} className="border border-[#2C2D35] bg-[#0d0e11] p-3">
            <div className="font-head font-bold text-2xl" style={{ color }}>{val}</div>
            <div className="font-mono text-[9px] text-[#9CA3AF] uppercase leading-tight mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="border border-[#2C2D35]">
        <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#10B981]" />
          <span className="font-head text-xs font-semibold text-[#10B981]">COVERAGE AUDIT</span>
          {isSup && (
            <button data-testid="run-coverage-audit-btn" onClick={runAudit} disabled={busy}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-head bg-[#10B981] text-black hover:bg-[#059669] disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Run Coverage Audit
            </button>
          )}
        </div>
        <div className="p-3">
          {!audit && <div className="font-mono text-[11px] text-[#9CA3AF]">No audit run yet. The audit verifies every active call type has a category, stable code, valid default priority (0–5), a question tree with location + callback questions, and a response recommendation.</div>}
          {audit && (
            <div data-testid="audit-result">
              <div className="flex items-center gap-3 font-mono text-[11px]">
                <span className={`font-head font-bold text-sm ${audit.result === "PASS" ? "text-[#10B981]" : "text-[#F59E0B]"}`}>{audit.result}</span>
                <span>{audit.total_checked} types checked</span>
                <span>{audit.issues_count} issue(s)</span>
                <span className="text-[#9CA3AF]">by {audit.by} · {(audit.ts || "").slice(0, 16)}</span>
              </div>
              {(audit.issues || []).slice(0, 30).map((i) => (
                <div key={i.code} className="font-mono text-[10px] text-[#F59E0B] mt-1">{i.code} {i.label}: {i.problems.join(", ")}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isSup && (
        <div className="border border-[#2C2D35]">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] flex items-center gap-2">
            <SearchX size={14} className="text-[#F59E0B]" />
            <span className="font-head text-xs font-semibold text-[#F59E0B]">POSSIBLE MISSING CALL TYPES — searches that returned no result</span>
          </div>
          <div className="p-3 space-y-2">
            {misses.length === 0 && <div className="font-mono text-[11px] text-[#9CA3AF]">Queue is empty — every recent search matched a call type.</div>}
            {misses.map((m) => (
              <div key={m.mid} data-testid={`miss-${m.mid}`} className="flex flex-wrap items-center gap-2 border border-[#2C2D35] bg-[#141518] px-2 py-1.5">
                <span className="font-mono text-[11px] text-white">"{m.term}"</span>
                <span className="font-mono text-[10px] text-[#9CA3AF]">×{m.count} · last {String(m.last_ts || "").slice(0, 16)}</span>
                {isAdmin && (
                  <div className="ml-auto flex items-center gap-1.5">
                    <input data-testid={`miss-label-${m.mid}`} className={`${inp} w-44`} placeholder="New type label"
                      value={promote[m.mid]?.label ?? m.term} onChange={(e) => setPromote({ ...promote, [m.mid]: { ...promote[m.mid], label: e.target.value } })} />
                    <select className={inp} value={promote[m.mid]?.discipline || "police"}
                      onChange={(e) => setPromote({ ...promote, [m.mid]: { ...promote[m.mid], discipline: e.target.value } })}>
                      <option value="police">Police</option><option value="fire">Fire</option><option value="ems">EMS</option>
                    </select>
                    <select className={inp} value={promote[m.mid]?.priority ?? 3}
                      onChange={(e) => setPromote({ ...promote, [m.mid]: { ...promote[m.mid], priority: e.target.value } })}>
                      {[0, 1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>P{p}</option>)}
                    </select>
                    <button data-testid={`miss-promote-${m.mid}`} onClick={() => doPromote(m)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-head bg-[#3B82F6] text-white hover:bg-[#2563eb]">
                      <ArrowUpCircle size={11} /> Create Type
                    </button>
                    <button data-testid={`miss-dismiss-${m.mid}`} onClick={() => doDismiss(m)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white">
                      <XCircle size={11} /> Dismiss
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="font-mono text-[9px] text-[#6B7280]">Follow agency policy and approved emergency dispatch protocols. AI/system suggestions never replace dispatcher classification.</div>
    </div>
  );
};
